/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo } from 'react';
import { Modal, DatePicker, Button, Space, ConfigProvider, Select } from 'antd';
import antdEnGb from 'antd/es/locale/en_GB';
import datePickerEnGb from 'antd/es/date-picker/locale/en_GB';

import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';

import { ScheduleModalProps } from './types';
import './scheduleModal.scss';

dayjs.locale('en-gb');

const generateTimeOptions = () => Array.from({ length: 24 * 4 }, (_, i) => {
    const totalMins = i * 15;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    return { label, value: label };
});

const timeOptions = generateTimeOptions();

const customDatePickerLocale = {
    ...datePickerEnGb,
    lang: {
        ...datePickerEnGb.lang,
        shortWeekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    },
};

const today = dayjs().startOf('day');
const sixMonthsFromNow = dayjs().add(6, 'month').endOf('day');

const ScheduleModal: React.FC<ScheduleModalProps> = ({
    visible,
    date,
    time,
    onDateChange,
    onTimeChange,
    onSave,
    onCancel,
}) => {
    const filteredTimeOptions = useMemo(() => {
        if (date && date.isSame(dayjs(), 'day')) {
            const currentHour = dayjs().hour();
            const currentMinute = dayjs().minute();

            return timeOptions.filter(option => {
                const [hours, minutes] = option.value.split(':').map(Number) as [number, number];
                return hours > currentHour || (hours === currentHour && minutes > currentMinute);
            });
        }
        return timeOptions;
    }, [date]);

    return (
        <ConfigProvider locale={antdEnGb}>
            <Modal
                title="Schedule News Update"
                open={visible}
                onCancel={onCancel}
                footer={[
                    <div className="schedule-modal-footer">
                        <Button key="save" className="schedule-modal-save-button" onClick={onSave}>
                            Save
                        </Button>
                        <Button key="cancel" className="schedule-modal-cancel-button" onClick={onCancel}>
                            Cancel
                        </Button>
                    </div>,
                ]}
                closable
                maskClosable={false}
                centered
                width={380}
                className="schedule-modal">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div className="schedule-calendar-container">
                        <label>Date</label>
                        <DatePicker
                            value={date}
                            onChange={onDateChange}
                            format="DD/MM/YYYY"
                            locale={customDatePickerLocale}
                            disabledDate={(current) => current && (current < today || current > sixMonthsFromNow)}
                            className="schedule-custom-datepicker"
                            popupClassName="schedule-calendar-popup"
                            placement="bottomRight"
                            style={{ width: '100%', height: 40, border: '1px solid #83ACCF' }} />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                        <label>Time</label>
                        <Select
                            showSearch={false}
                            options={filteredTimeOptions}
                            value={time ? time.format('HH:mm') : undefined}
                            placeholder="Select time"
                            onChange={(value) => {
                                if (typeof value === 'string') {
                                    const parts = value.split(':');
                                    if (parts.length === 2) {
                                        const hours = Number(parts[0]);
                                        const minutes = Number(parts[1]);
                                        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                                            const base = date ? date.clone() : dayjs();
                                            onTimeChange(base.hour(hours).minute(minutes).second(0));
                                        }
                                    }
                                }
                            }}
                            style={{ width: '100%', height: 40, marginTop: 4 }} />
                    </div>
                </Space>
            </Modal>
        </ConfigProvider>
    );
};

export default ScheduleModal;
